import React, { useEffect, useRef } from 'react';

import { Box } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useOneOnOneAgendaLibraryAgendaCondition } from 'screens/oneOnOneRabbit/InstancePage/slice/OneOnOneInstancePageSlice';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { isNetchexLogin } from 'constants/netChexConstants';

import { AgendaLibrary } from '../InstancePage/features/AgendaLibrary/AgendaLibrary';
import { RouteLeavingGuard } from './RouteLeavingGuard';
import Footer from './components/Footer';
import { AGENDA_CONDITIONS_TYPE } from './constants';
import { OneOnOneSettingsPageReactQueryKeys } from './constants/OneOnOneSettingsPageReactQueryKeys';
import { AgendaPoints } from './features/AgendaPoints/AgendaPoints';
import { createAgendaObject } from './features/AgendaPoints/components/CreateAdminAgenda';
import { EventTemplateAgendaType } from './features/AgendaPoints/services';
import { GeneralSettings } from './features/GeneralSetttings/GeneralSettings';
import { AdminAgendaType } from './interfaces/AdminAgenda';
import {
  resetIdsToBeDeleted,
  setSaveButtonIsDisabled,
  useSaveButtonIsDisabled,
} from './slice/OneOnOneEventBasedAgendaSlice';

interface Props {}

const SettingsPage = (props: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const saveButtonRef = useRef<HTMLButtonElement>();

  const isSaveButtonDisabled = useSaveButtonIsDisabled();

  useEffect(
    () => () => {
      // resting the ids to be deleted to empty array
      dispatch(resetIdsToBeDeleted());
      dispatch(
        setSaveButtonIsDisabled({
          saveButtonIsDisabled: true,
        })
      );
    },
    []
  );

  // agenda library

  const queryClient = useQueryClient();

  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);

  const orgId = Number(loggedInEmployeeData?.organization?.id);

  const agendaCondition = useOneOnOneAgendaLibraryAgendaCondition();

  const createAgendaHandler = async (agendaText: string, agendaCondition: AGENDA_CONDITIONS_TYPE) => {
    if (!agendaText || agendaText.trim() === '') {
      return;
    }
    try {
      // sendClickedOnOneOnOneAddedAdminAgendaPoints();
      const newAgenda = createAgendaObject({
        agendaText,
        agendaCondition,
        orgId,
      });

      if (agendaCondition === 'REGULAR') {
        queryClient.setQueryData<AdminAgendaType[]>(
          [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'REGULAR'],
          (oldData) => {
            const agendaToBeAdded = { ...newAgenda, displayOrder: oldData?.length ?? 0 };
            return [...(oldData || []), agendaToBeAdded];
          }
        );
      } else {
        queryClient.setQueryData<EventTemplateAgendaType>(
          [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'],
          (oldData) => {
            const agendaToBeAdded = {
              ...newAgenda,
              displayOrder: oldData[agendaCondition]?.length ?? 0,
            };
            return {
              ...oldData,
              [agendaCondition]: [...(oldData[agendaCondition] || []), agendaToBeAdded],
            };
          }
        );
      }
    } catch (e) {
      console.error('Error creating agenda : ', e);
    } finally {
      dispatch(
        setSaveButtonIsDisabled({
          saveButtonIsDisabled: false,
        })
      );
    }
  };

  const isNewNavEnabled = checkNewNavEnabled();

  return (
    <Box
      display="flex"
      flexDir="column"
      position="relative"
      height={isNetchexLogin ? null : isNewNavEnabled ? 'calc(100vh - 115px)' : 'calc(100vh - 155px)'}
      paddingBottom={isNetchexLogin ? '80px' : null}
    >
      <RouteLeavingGuard
        when={!isSaveButtonDisabled}
        shouldBlockNavigation={(location) => {
          if (!isSaveButtonDisabled) {
            return true;
          }
          return false;
        }}
        navigate={(path) => history.push(path)}
        onSave={() => {
          if (saveButtonRef.current) {
            saveButtonRef?.current?.click();
          }
        }}
      />
      <div
        style={{
          flex: '1',
          overflow: 'auto',
          padding: isNewNavEnabled ? '1px' : '16px',
          margin: isNewNavEnabled ? '4px -1px -1px -1px' : '4px 0 0 0',
        }}
      >
        <GeneralSettings />
        <AgendaPoints />
        <AgendaLibrary
          isSettingsPage
          onAgendaClick={(agenda) => {
            createAgendaHandler(agenda?.agendaText, agendaCondition);
          }}
        />
      </div>
      <Box
        width={isNewNavEnabled ? 'calc(100% + 32px)' : '100%'}
        display="flex"
        mx={isNewNavEnabled ? '-16px' : undefined}
      >
        <Footer saveButtonRef={saveButtonRef} />
      </Box>
    </Box>
  );
};

export { SettingsPage };
